import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { Bandeau, Champ, Modale } from './ui';
import type { Client, Verdict } from '../api/types';
import { T } from '../i18n';

const VIDE = {
  type: 'particulier', nom: '', prenom: '', raisonSociale: '',
  rc: '', nif: '', nis: '', articleImposition: '',
  dateNaissance: '', lieuNaissance: '', telephone: '', email: '', adresse: '',
  pieceType: 'CIN', pieceNumero: '', pieceDelivreeLe: '', pieceDelivreeA: '',
  permisNumero: '', permisDelivreLe: '', compteATerme: false,
};

const jour = (d?: string) => (d ? String(d).slice(0, 10) : '');

/**
 * Creation ET modification de client.
 *
 * Le permis est capture SEPAREMENT de la piece d'identite : le contrat papier
 * n'a qu'un champ pour les deux, et sans date de permis l'article 01 est
 * inverifiable. Le contrat imprime, lui, garde la ligne unique.
 *
 * La modification existe pour une raison precise : une date de naissance mal
 * saisie bloque le client a l'article 01 a chaque location, pour toujours. La
 * demande de correction pouvait etre accordee — mais rien, nulle part, ne
 * permettait ensuite de changer la valeur.
 */
export function FormulaireClient({ client, onCree, onFermer }:
  { client?: Client; onCree: (c: Client, v: Verdict) => void; onFermer: () => void }) {
  const modification = Boolean(client);
  const [f, setF] = useState(() => (client
    ? {
      ...VIDE,
      type: client.type ?? 'particulier',
      nom: client.nom ?? '', prenom: client.prenom ?? '', raisonSociale: client.raisonSociale ?? '',
      rc: client.rc ?? '', nif: client.nif ?? '', nis: client.nis ?? '',
      articleImposition: client.articleImposition ?? '',
      dateNaissance: jour(client.dateNaissance), lieuNaissance: client.lieuNaissance ?? '',
      telephone: client.telephone ?? '', email: client.email ?? '', adresse: client.adresse ?? '',
      pieceType: client.pieceType ?? 'CIN', pieceNumero: client.pieceNumero ?? '',
      pieceDelivreeLe: jour(client.pieceDelivreeLe), pieceDelivreeA: client.pieceDelivreeA ?? '',
      permisNumero: client.permisNumero ?? '', permisDelivreLe: jour(client.permisDelivreLe),
      compteATerme: client.compteATerme ?? false,
    }
    : { ...VIDE }));
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const societe = f.type === 'societe';
  const F = T.formulaireClient;

  /*
   * Ce qui manque n'empeche PAS d'enregistrer — c'est montre en rouge.
   *
   * Une fiche client se remplit au comptoir, souvent en deux fois : le client
   * appelle pour tenir une voiture, il passera demain avec ses papiers. Bloquer
   * la creation obligeait a inventer un numero de telephone ou de piece pour
   * pouvoir avancer — et une fois la fausse valeur saisie, plus rien ne
   * signalait qu'elle etait fausse. Un champ vide en rouge dit la verite ;
   * un champ rempli au hasard ment pour toujours.
   *
   * Le seul champ qui reste exige est le nom : sans lui, la fiche est
   * introuvable et ne sert a rien.
   */
  const vide = (x: string) => !x.trim();
  const manqueNom = vide(societe ? f.raisonSociale : f.nom);
  const manqueTelephone = vide(f.telephone);
  const manquePiece = vide(f.pieceNumero);
  const manqueNaissance = !societe && !f.dateNaissance;
  const manquePermis = !societe && !f.permisDelivreLe;
  // L'article 01 n'est PAS derogeable quand la donnee est absente : sans ces
  // deux dates, aucune location ne pourra etre enregistree. On le dit ici,
  // au moment ou la fiche se cree, pas au comptoir devant le client.
  const article01Impossible = manqueNaissance || manquePermis;
  const aDesManques = manqueTelephone || manquePiece || article01Impossible;

  async function creer() {
    setErreur(''); setEnvoi(true);
    const corps = {
      ...f,
      email: f.email || undefined,
      dateNaissance: f.dateNaissance || undefined,
      pieceDelivreeLe: f.pieceDelivreeLe || undefined,
      permisDelivreLe: f.permisDelivreLe || undefined,
    };
    try {
      if (modification) {
        const maj = await api.patch<Client>(`/clients/${client!._id}`, corps);
        // Le verdict est recalcule : c'est tout l'interet de la correction.
        const v = await api.post<Verdict>('/clients/verifier', {
          dateNaissance: maj.dateNaissance, permisDelivreLe: maj.permisDelivreLe,
          listeNoire: maj.listeNoire, motifListeNoire: maj.motifListeNoire,
        });
        onCree(maj, v);
      } else {
        const r = await api.post<{ client: Client; verdict: Verdict }>('/clients', corps);
        onCree(r.client, r.verdict);
      }
    } catch (e) {
      setErreur(e instanceof ApiError ? e.erreur : modification ? F.modificationImpossible : F.creationImpossible);
    } finally { setEnvoi(false); }
  }

  return (
    <Modale titre={modification ? F.modifierTitre(client!.nom) : F.nouveauClient} onFermer={onFermer}>
      <div style={{ display: 'grid', gap: 12, maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
        <Champ label={F.type}>
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
            <option value="particulier">{F.particulier}</option><option value="societe">{F.societe}</option>
          </select>
        </Champ>

        {societe ? (
          <>
            <Champ label={F.raisonSociale} erreur={manqueNom ? F.raisonSocialeObligatoire : undefined}>
              <input value={f.raisonSociale} onChange={(e) => setF({ ...f, raisonSociale: e.target.value, nom: e.target.value })} />
            </Champ>
            <label style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
              <input type="checkbox" checked={f.compteATerme} onChange={(e) => setF({ ...f, compteATerme: e.target.checked })} />
              {F.compteATerme}
            </label>
            {/* Une facture a une societe porte SES identifiants fiscaux :
              * sans eux, elle n'est pas opposable et le client la refuse. */}
            <div className="grille g2">
              <Champ label={F.registreCommerce}>
                <input value={f.rc} onChange={(e) => setF({ ...f, rc: e.target.value })} />
              </Champ>
              <Champ label={F.nif}>
                <input value={f.nif} onChange={(e) => setF({ ...f, nif: e.target.value })} />
              </Champ>
              <Champ label={F.nis}>
                <input value={f.nis} onChange={(e) => setF({ ...f, nis: e.target.value })} />
              </Champ>
              <Champ label={F.articleImposition}>
                <input value={f.articleImposition} onChange={(e) => setF({ ...f, articleImposition: e.target.value })} />
              </Champ>
            </div>
            <p className="mini sourdine" style={{ margin: 0 }}>
              {F.quatreLignes}
            </p>
          </>
        ) : (
          <div className="grille g2">
            <Champ label={F.nom} erreur={manqueNom ? F.nomObligatoire : undefined}>
              <input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} />
            </Champ>
            <Champ label={F.prenom}><input value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })} /></Champ>
            <Champ label={F.dateNaissance}
                   aide={F.dateNaissanceAide}
                   erreur={manqueNaissance ? F.dateNaissanceManquante : undefined}>
              <input type="date" value={f.dateNaissance} onChange={(e) => setF({ ...f, dateNaissance: e.target.value })} />
            </Champ>
            <Champ label={F.lieuNaissance}><input value={f.lieuNaissance} onChange={(e) => setF({ ...f, lieuNaissance: e.target.value })} /></Champ>
          </div>
        )}

        <div className="grille g2">
          <Champ label={F.telephone}
                 erreur={manqueTelephone ? F.telephoneManquant : undefined}>
            <input value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} placeholder="0661…" />
          </Champ>
          <Champ label={F.email}><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Champ>
        </div>
        <Champ label={F.adresse}><input value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} /></Champ>

        <div className="grille g2">
          <Champ label={F.typePiece}>
            <select value={f.pieceType} onChange={(e) => setF({ ...f, pieceType: e.target.value })}>
              <option value="CIN">{F.pieceCin}</option><option value="PC">{F.piecePc}</option>
              <option value="PSP">{F.piecePsp}</option><option value="CM">{F.pieceCm}</option>
            </select>
          </Champ>
          <Champ label={F.numeroPiece}
                 erreur={manquePiece ? F.pieceManquante : undefined}>
            <input value={f.pieceNumero} onChange={(e) => setF({ ...f, pieceNumero: e.target.value })} />
          </Champ>
          <Champ label={F.delivreeLe}><input type="date" value={f.pieceDelivreeLe} onChange={(e) => setF({ ...f, pieceDelivreeLe: e.target.value })} /></Champ>
          <Champ label={F.delivreeA}><input value={f.pieceDelivreeA} onChange={(e) => setF({ ...f, pieceDelivreeA: e.target.value })} /></Champ>
        </div>

        {!societe && (
          <div className="grille g2">
            <Champ label={F.numeroPermis}><input value={f.permisNumero} onChange={(e) => setF({ ...f, permisNumero: e.target.value })} /></Champ>
            <Champ label={F.permisDelivreLe}
                   aide={F.permisAide}
                   erreur={manquePermis ? F.permisManquant : undefined}>
              <input type="date" value={f.permisDelivreLe} onChange={(e) => setF({ ...f, permisDelivreLe: e.target.value })} />
            </Champ>
          </div>
        )}
      </div>

      {aDesManques && (
        <div style={{ marginTop: 10 }}>
          <Bandeau type="ko" titre={F.ficheIncomplete}>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {manqueTelephone && <li>{F.pasTelephone}</li>}
              {manquePiece && <li>{F.pasPiece}</li>}
              {article01Impossible && (
                <li>
                  {manqueNaissance && manquePermis ? F.niNaissanceNiPermis
                    : manqueNaissance ? F.pasNaissance : F.pasPermis}
                  {F.article01Sep}<b>{F.aucuneLocation}</b>{F.article01Fin}
                </li>
              )}
            </ul>
            <p style={{ margin: '8px 0 0' }}>
              {F.completez}
            </p>
          </Bandeau>
        </div>
      )}

      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{T.commun.actions.annuler}</button>
        <button className="btn btn-p" disabled={envoi || manqueNom}
                onClick={() => void creer()}>
          {envoi ? F.enregistrement : modification ? F.enregistrerCorrections : F.creerClient}
        </button>
      </div>
    </Modale>
  );
}
